/**
 * Single row used inside the left Review pane. Reused — verbatim — by
 * the field-scoped Inspector "Review items for this field" section.
 *
 * Click selects the item via `onSelect`, which the parent threads
 * into `useFieldSelection`. Active state (`isActive`) gives visual
 * feedback when this row matches the current selection.
 */
import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { ReviewItem } from '../hooks/useReviewItems';
import { SeverityIcon, KIND_LABELS } from '../inspector/decisions/components';
import { confidenceColor } from '../util/colors';

export interface ReviewItemRowProps {
  item: ReviewItem;
  isActive?: boolean;
  onSelect?: (item: ReviewItem) => void;
}

export const ReviewItemRow: React.FC<ReviewItemRowProps> = ({
  item,
  isActive = false,
  onSelect,
}) => {
  const decisionKind = item.decision_kind;
  const kindLabel = decisionKind ? KIND_LABELS[decisionKind]?.label : null;

  const ariaLabel = [
    `${item.severity} ${item.bucket}`,
    item.target_field
      ? `${item.target_table}.${item.target_field}`
      : item.target_table,
    item.message,
  ].join(' — ');

  return (
    <Box
      data-row-id={item.id}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-current={isActive ? 'true' : undefined}
      onClick={() => onSelect?.(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(item);
        }
      }}
      sx={{
        px: 0.75,
        py: 0.5,
        cursor: 'pointer',
        outline: 'none',
        borderLeft: (theme) =>
          `3px solid ${
            item.severity === 'error'
              ? theme.palette.error.main
              : item.severity === 'warning'
                ? theme.palette.warning.main
                : theme.palette.info.main
          }`,
        bgcolor: isActive ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: isActive ? 'action.selected' : 'action.hover' },
        '&:focus-visible': {
          boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`,
        },
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      {/* Line 1: severity icon + target.field + small chips on the right */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
        <SeverityIcon severity={item.severity} />
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600, fontSize: 12, flex: 1, minWidth: 0 }}
        >
          {item.target_field
            ? `${item.target_table}.${item.target_field}`
            : item.target_table}
        </Typography>
        {typeof item.confidence === 'number' && (
          <Typography
            variant="caption"
            sx={{
              color: confidenceColor(item.confidence),
              fontWeight: 700,
              fontSize: 10,
              flexShrink: 0,
            }}
          >
            {Math.round(item.confidence * 100)}%
          </Typography>
        )}
        {kindLabel && (
          <Chip
            label={kindLabel}
            size="small"
            color="info"
            sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: 0.5 }, flexShrink: 0 }}
          />
        )}
        <Chip
          label={item.bucket}
          size="small"
          variant="outlined"
          sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: 0.5 }, flexShrink: 0 }}
        />
      </Stack>
      {/* Line 2: message (single line, ellipsis), with optional source hint. */}
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ display: 'block', fontSize: 11, lineHeight: 1.3, mt: 0.25 }}
      >
        {item.source_field
          ? `${item.source_file ?? '?'}.${item.source_field} — ${item.message}`
          : item.message}
      </Typography>
    </Box>
  );
};

export default ReviewItemRow;
